import React, { useEffect } from 'react'

import { Button, Fieldset, Input, ModalBox, RadioButton } from '@fourthwall/components'
import { useFormik } from '@fourthwall/utils/dist/formik'

import type { SelectableRole } from '../../Team.types'
import { TeamMember } from '../TeamMember/TeamMember'
import * as S from './RoleModal.styled'
import type { FormValues } from './RoleModal.types'
import { validationSchema } from './consts'

export type RoleModalProps = {
  type?: 'invitation' | 'edit'
  name?: string
  initialValues?: FormValues
  selectableRoles: SelectableRole[]
  close: () => void
  onSubmit: (values: FormValues) => void
}

export const RoleModal = ({
  type = 'invitation',
  name,
  initialValues,
  selectableRoles,
  close,
  onSubmit,
}: RoleModalProps) => {
  const inputRef = React.useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }, [inputRef])

  const formik = useFormik<FormValues>({
    initialValues: {
      email: initialValues?.email,
      role: initialValues?.role || selectableRoles[0].checkedValue,
    },
    validationSchema,
    onSubmit: (values: FormValues) => {
      onSubmit(values)
      close()
    },
  })

  const getTitle = () => {
    if (type === 'invitation') {
      return 'Invite new team member'
    }

    return 'Change team member role'
  }

  const getSubmitLabel = () => {
    if (type === 'invitation') {
      return 'Invite'
    }

    return 'Change'
  }

  const renderInput = () => {
    if (type === 'invitation') {
      return (
        <Input
          inputRef={inputRef}
          type="email"
          label="Email address"
          fullWidth
          {...formik.getFieldProps('email')}
        />
      )
    }

    return (
      formik.values.email && (
        <TeamMember
          teamMember={{ name: name || '', email: formik.values.email, roleName: '', isYou: false }}
        />
      )
    )
  }

  return (
    <form onSubmit={formik.handleSubmit}>
      <ModalBox
        title={getTitle()}
        buttons={[
          <Button label="Cancel" appearance="secondary" onClick={close} />,
          <Button
            label={getSubmitLabel()}
            appearance="primary"
            type="submit"
            disabled={!formik.isValid || !formik.dirty}
          />,
        ]}
        size="small"
        onClose={close}
      >
        <Fieldset>
          <Fieldset.Row>
            <S.InputContainer>{renderInput()}</S.InputContainer>
          </Fieldset.Row>
          <S.RolesContainer>
            <S.RolesTitle>Select a role</S.RolesTitle>
            {selectableRoles.map((selectableRole) => (
              <Fieldset.Row key={selectableRole.checkedValue}>
                <RadioButton {...selectableRole} {...formik.getFieldProps('role')} />
              </Fieldset.Row>
            ))}
          </S.RolesContainer>
        </Fieldset>
      </ModalBox>
    </form>
  )
}
